/**
 * ChangeItemStatusModal Component
 * 
 * Modal for changing order item status
 * Allows arbitrator to manually change the status of a disputed item
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Stack
} from '@mui/material';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

interface ChangeItemStatusModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (payload: { newStatus: string; reason: string }) => void;
  disputeTitle: string;
  currentStatus?: string;
  loading: boolean;
  error: string | null;
}

// Available statuses for order items (matches OrderItemStatus enum in backend)
const ITEM_STATUSES = [
  { value: 'pending', label: 'در انتظار', color: '#ff9800' },
  { value: 'processing', label: 'در حال پردازش', color: '#2196f3' },
  { value: 'delivered', label: 'تحویل داده شده', color: '#4caf50' },
  { value: 'disputed', label: 'اختلاف دارد', color: '#e91e63' },
  { value: 'cancelled', label: 'لغو شده', color: '#f44336' },
  { value: 'refunded', label: 'بازپرداخت شده', color: '#9c27b0' }
];

const ChangeItemStatusModal: React.FC<ChangeItemStatusModalProps> = ({
  open,
  onClose,
  onConfirm,
  disputeTitle,
  currentStatus = 'pending',
  loading,
  error
}) => {
  const [newStatus, setNewStatus] = useState<string>('');
  const [reason, setReason] = useState<string>('');

  const handleSubmit = () => {
    console.log('🔄 ChangeItemStatusModal handleSubmit:', { newStatus, reason, currentStatus });
    if (!newStatus) {
      console.warn('❌ No new status selected');
      return;
    }
    onConfirm({ newStatus, reason });
  };

  const handleClose = () => {
    if (!loading) {
      setNewStatus('');
      setReason('');
      onClose();
    }
  };

  const isValid = newStatus && newStatus !== currentStatus;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      dir="rtl"
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}>
        <SwapHorizIcon color="primary" />
        <Typography variant="h6">تغییر وضعیت آیتم سفارش</Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          اختلاف: <strong>{disputeTitle}</strong>
        </Typography>

        {/* Current → New transition preview — once both are picked, the admin
            sees EXACTLY what change will be applied before clicking confirm. */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems="center"
          sx={{
            mb: 3,
            p: 2,
            bgcolor: 'grey.50',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          {/* Current */}
          <Box sx={{ flex: 1, textAlign: 'center', minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              وضعیت فعلی
            </Typography>
            <Box
              sx={{
                display: 'inline-block',
                mt: 0.5,
                px: 2,
                py: 0.75,
                borderRadius: 1,
                bgcolor: ITEM_STATUSES.find(s => s.value === currentStatus)?.color || '#666',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '0.85rem'
              }}
            >
              {ITEM_STATUSES.find(s => s.value === currentStatus)?.label || currentStatus}
            </Box>
          </Box>

          {/* Arrow */}
          <ArrowBackIcon
            sx={{
              color: newStatus ? 'primary.main' : 'text.disabled',
              fontSize: 32,
              transform: { xs: 'rotate(-90deg)', sm: 'none' }
            }}
          />

          {/* New */}
          <Box sx={{ flex: 1, textAlign: 'center', minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              وضعیت جدید
            </Typography>
            {newStatus ? (
              <Box
                sx={{
                  display: 'inline-block',
                  mt: 0.5,
                  px: 2,
                  py: 0.75,
                  borderRadius: 1,
                  bgcolor: ITEM_STATUSES.find(s => s.value === newStatus)?.color || '#666',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '0.85rem'
                }}
              >
                {ITEM_STATUSES.find(s => s.value === newStatus)?.label || newStatus}
              </Box>
            ) : (
              <Typography variant="body2" color="text.disabled" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                انتخاب نشده
              </Typography>
            )}
          </Box>
        </Stack>

        {/* Effect callout — explain what the transition triggers (close dispute, mark refunded, etc.) */}
        {newStatus && (
          <Alert severity="info" sx={{ mb: 3 }}>
            با تأیید، وضعیت آیتم به <strong>«{ITEM_STATUSES.find(s => s.value === newStatus)?.label}»</strong> تغییر می‌کند.
            {newStatus === 'refunded' && ' آیتم به‌عنوان بازپرداخت شده ثبت و اختلاف بسته می‌شود.'}
            {newStatus === 'cancelled' && ' آیتم لغو و اختلاف بسته می‌شود.'}
            {newStatus === 'delivered' && ' آیتم به‌عنوان تحویل شده ثبت و اختلاف بسته می‌شود.'}
            {newStatus === 'processing' && ' آیتم به مرحله پردازش برمی‌گردد و اختلاف بسته می‌شود.'}
            {newStatus === 'disputed' && ' آیتم در حالت اختلاف باقی می‌ماند.'}
            {newStatus === 'pending' && ' آیتم به وضعیت اولیه بازمی‌گردد.'}
          </Alert>
        )}

        {/* New Status Select */}
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>وضعیت جدید *</InputLabel>
          <Select
            value={newStatus}
            onChange={(e) => setNewStatus(e.target.value)}
            label="وضعیت جدید *"
            disabled={loading}
          >
            {ITEM_STATUSES.filter(s => s.value !== currentStatus).map((status) => (
              <MenuItem key={status.value} value={status.value}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      bgcolor: status.color
                    }}
                  />
                  {status.label}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Reason TextField */}
        <TextField
          fullWidth
          label="دلیل تغییر وضعیت"
          multiline
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="دلیل تغییر وضعیت را وارد کنید..."
          disabled={loading}
          helperText="این توضیح در تاریخچه اختلاف ثبت خواهد شد"
        />
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button onClick={handleClose} disabled={loading}>
          انصراف
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!isValid || loading}
          startIcon={loading ? <CircularProgress size={16} /> : <SwapHorizIcon />}
        >
          {loading ? 'در حال تغییر...' : 'تغییر وضعیت'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChangeItemStatusModal;

